import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import EngineerLayout from './EngineerLayout';
import { FaArrowLeft, FaPrint, FaSave, FaBuilding, FaUserTie, FaMapMarkerAlt, FaCalendarAlt, FaBriefcase, FaIdCard } from 'react-icons/fa';
import { toast } from 'react-toastify';

const EngineerSocietyDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const societyRef = ref(db, `ekamwatapzp/SocietyRegistrations/${id}`);
        const unsubscribe = onValue(societyRef, (snapshot) => {
            if (snapshot.exists()) {
                setFormData(snapshot.val());
            } else {
                toast.error("डेटा आढळला नाही");
                navigate(-1);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [id, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdate = async () => {
        try {
            const societyRef = ref(db, `ekamwatapzp/SocietyRegistrations/${id}`);
            await update(societyRef, formData);
            toast.success("प्रोफाइल अपडेट झाली!");
        } catch (error) {
            console.error(error);
            toast.error("अपडेट अयशस्वी!");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
                <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#085e53]"></div>
                    <p className="text-sm font-bold text-gray-500 animate-pulse tracking-widest uppercase">माहिती लोड होत आहे...</p>
                </div>
            </div>
        );
    }

    const DetailField = ({ label, name, value, type = "text", options = null, icon: Icon, fullWidth = false }) => (
        <div className={`flex flex-col gap-2 ${fullWidth ? 'md:col-span-3' : ''}`}>
            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                {Icon && <Icon className="text-[#085e53]" size={10} />}
                {label}
            </label>
            {options ? (
                <select
                    name={name}
                    value={value || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold focus:bg-white focus:border-[#085e53] transition-all outline-none text-gray-700 shadow-sm"
                >
                    <option value="">निवडा</option>
                    {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            ) : type === "textarea" ? (
                <textarea
                    name={name}
                    value={value || ''}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold focus:bg-white focus:border-[#085e53] transition-all outline-none text-gray-700 shadow-sm"
                />
            ) : (
                <input
                    type={type}
                    name={name}
                    value={value || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold focus:bg-white focus:border-[#085e53] transition-all outline-none text-gray-700 shadow-sm"
                />
            )}
        </div>
    );

    return (
        <EngineerLayout>
            <div className="p-6 md:p-8 bg-gray-50/30 min-h-screen">
                {/* Dark Header Card */}
                <div className="bg-[#1a1c23] rounded-3xl p-10 mb-8 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#085e53]/10 rounded-full -ml-16 -mb-16 blur-2xl"></div>
                    
                    <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-6">
                            <button 
                                onClick={() => navigate(-1)}
                                className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-2xl transition-all hover:scale-110 active:scale-90 backdrop-blur-sm border border-white/10"
                            >
                                <FaArrowLeft size={20} />
                            </button>
                            <div>
                                <h1 className="text-4xl font-bold text-white tracking-tight mb-3">सोसायटी अर्ज पहा</h1>
                                <p className="text-gray-400 font-bold text-sm tracking-widest uppercase">
                                    नोंदणी अर्जाचा तपशील आणि स्थिती
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <button 
                                onClick={() => window.print()}
                                className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-xl text-sm font-bold tracking-widest transition-all hover:scale-105 active:scale-95 flex items-center gap-3 backdrop-blur-sm border border-white/10"
                            >
                                <FaPrint /> प्रिंट
                            </button>
                        </div>
                    </div>
                </div>

                <div className="max-w-[1200px] mx-auto space-y-8 pb-12">
                    {/* Basic Info Card */}
                    <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
                        <div className="p-8 border-b border-gray-50 flex items-center gap-4 bg-gray-50/30">
                            <div className="p-3 bg-[#085e53] rounded-xl text-white">
                                <FaBuilding size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">संस्थेची माहिती</h2>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Basic Details</p>
                            </div>
                        </div>
                        <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                            <DetailField label="मजूर सहकारी संस्थेचे नाव" name="societyName" value={formData.societyName} icon={FaBuilding} />
                            <DetailField label="तालुका" name="taluka" value={formData.taluka} options={["हवेली", "तासगाव", "मिरज", "जत", "खानापूर", "कडेगाव"]} icon={FaMapMarkerAlt} />
                            <DetailField label="नोंदणी क्रमांक" name="registrationNo" value={formData.registrationNo} icon={FaIdCard} />
                            
                            <DetailField label="नोंदणी जिल्हा" name="registrationDistrict" value={formData.registrationDistrict} icon={FaMapMarkerAlt} />
                            <DetailField label="उपनिबंधक / सहाय्यक उपनिबंधक" name="registrarOffice" value={formData.registrarOffice} icon={FaUserTie} />
                            <DetailField label="नोंदणी दिनांक" name="registrationDate" value={formData.registrationDate} type="date" icon={FaCalendarAlt} />
                            
                            <DetailField label="मजूर सहकारी संस्थेचे संपूर्ण पत्ता" name="societyAddress" value={formData.societyAddress} type="textarea" fullWidth icon={FaMapMarkerAlt} />
                        </div>
                    </div>

                    {/* Technical Info Card */}
                    <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
                        <div className="p-8 border-b border-gray-50 flex items-center gap-4 bg-gray-50/30">
                            <div className="p-3 bg-amber-500 rounded-xl text-white">
                                <FaBriefcase size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">तांत्रिक आणि आर्थिक माहिती</h2>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Financial & Technical</p>
                            </div>
                        </div>
                        <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                            <DetailField label="आर्थिक स्थैर्य" name="economicStability" value={formData.economicStability} icon={FaBriefcase} />
                            <DetailField label="स्वतःचे भाग भांडवल" name="ownCapital" value={formData.ownCapital} icon={FaBriefcase} />
                            <DetailField label="शासकीय सहभाग (रू)" name="govtShare" value={formData.govtShare} icon={FaBriefcase} />
                            
                            <DetailField label="नोंदणी हवी असलेला वर्ग" name="registrationClass" value={formData.registrationClass} options={["अ अकुशल", "ब अकुशल", "क अकुशल"]} icon={FaIdCard} />
                            <DetailField label="तपासणी वर्ग" name="inspectionClass" value={formData.inspectionClass} options={["अ वर्ग", "ब वर्ग", "क वर्ग"]} icon={FaIdCard} />
                            <DetailField label="इतर खात्याकडे वर्गीकरण" name="otherClassification" value={formData.otherClassification} icon={FaBriefcase} />

                            <DetailField label="इतर संस्थेत सभासद आहे का?" name="memberElsewhere" value={formData.memberElsewhere} options={["हो", "नाही"]} icon={FaUserTie} />
                        </div>
                    </div>

                    {/* Chairman Info Card */}
                    <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
                        <div className="p-8 border-b border-gray-50 flex items-center gap-4 bg-gray-50/30">
                            <div className="p-3 bg-blue-500 rounded-xl text-white">
                                <FaUserTie size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">चेअरमेनची माहिती</h2>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Chairman Details</p>
                            </div>
                        </div>
                        <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                            <DetailField label="चेअरमेनचे नाव" name="chairmanName" value={formData.chairmanName} icon={FaUserTie} />
                            <DetailField label="व्हॉट्सॲप नंबर" name="chairmanWhatsapp" value={formData.chairmanWhatsapp} icon={FaUserTie} />
                            <DetailField label="चेअरमेनचे पत्ता" name="chairmanAddress" value={formData.chairmanAddress} type="textarea" icon={FaMapMarkerAlt} />
                        </div>
                    </div>

                    {/* Documents Info Card */}
                    <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
                        <div className="p-8 border-b border-gray-50 flex items-center gap-4 bg-gray-50/30">
                            <div className="p-3 bg-rose-500 rounded-xl text-white">
                                <FaIdCard size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">ओळख आणि दस्तऐवज क्रमांक</h2>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">ID & Registration Nos</p>
                            </div>
                        </div>
                        <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                            <DetailField label="संस्थेचा आधार नं." name="societyAadhar" value={formData.societyAadhar} icon={FaIdCard} />
                            <DetailField label="संस्थेचा पॅन नं." name="societyPan" value={formData.societyPan} icon={FaIdCard} />
                            <DetailField label="जी.एस.टी. नंबर" name="societyGst" value={formData.societyGst} icon={FaIdCard} />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center pt-8">
                        <button 
                            onClick={handleUpdate}
                            className="flex items-center gap-3 bg-[#085e53] hover:bg-[#064a41] text-white px-12 py-4 rounded-2xl text-lg font-bold tracking-widest transition-all hover:scale-105 active:scale-95 shadow-2xl shadow-[#085e53]/20 uppercase"
                        >
                            <FaSave /> माहिती जतन करा
                        </button>
                    </div>
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerSocietyDetails;
